import type { CulturalOfferingDTO } from '@ui/shared/util-api';
import type { CultureMapItemModel } from '../entities/culture-map-item.model';
import { CultureMapOfferingItemModel } from '../entities/culture-map-offering-item.model';
import { CulturalFacilityMapper } from './cultural-facility-mapper';
import { CulturalTargetGroupMapper } from './cultural-target-group-mapper';
import { CulturalElevatorAccessMapper } from './cultural-elevator-access-mapper';
import { CulturalInterpreterAccessMapper } from './cultural-interpreter-access-mapper';
import { CulturalUsageTypeMapper } from './cultural-usage-type-mapper';
import { CulturalExperienceTypeMapper } from './cultural-experience-type-mapper';

export class CulturalOfferingMapper {
    public static fromDto(offering: CulturalOfferingDTO): CultureMapItemModel {
        return new CultureMapOfferingItemModel({
            id: offering.id,
            uniqueId: `${ offering.id }-offering`,
            title: offering.title,
            location: offering.location,
            short_description: offering.short_description,
            iconPath: offering.icon ?? '',
            linkedItems: [CulturalFacilityMapper.fromDto(offering.facility)],

            imagePath: offering.image?.thumbs?.detail ?? undefined,
            thumbnailPath: offering.image?.thumbs?.preview ?? offering.image?.thumbs?.detail ?? undefined,

            cultureSectorDescription: offering.culture_sector_description,
            targetGroups: offering.target_groups
                .map(dto => CulturalTargetGroupMapper.fromDto(dto))
                .sort((a, b) => (a.id > b.id ? 1 : -1)),
            cultureExperienceTypes: offering.culture_experience_types.map(dto => CulturalExperienceTypeMapper.fromDto(dto)),
            cultureUsageType: CulturalUsageTypeMapper.toLabel(offering.culture_usage_type),
            languages: offering.languages ?? undefined,
            contact: {
                id: offering.contact.id,
                position: offering.contact.contact_function,
                firstName: offering.contact.first_name,
                lastName: offering.contact.last_name,
                phone: offering.contact.phone,
                email: offering.contact.email,
            },
            accessibilityFeatures: {
                id: offering.accessibility_features.id,
                locationAccess: offering.accessibility_features.location_access ?? undefined,
                elevatorAccess: CulturalElevatorAccessMapper.toLabel(offering.accessibility_features.elevator_access),
                toiletAccess: offering.accessibility_features.toilet_access ?? undefined,
                toiletNursingBed: offering.accessibility_features.toilet_nursing_bed ?? undefined,
                inductionLoop: offering.accessibility_features.induction_loop ?? undefined,
                audioDescription: offering.accessibility_features.audio_description ?? undefined,
                audioGuide: offering.accessibility_features.audio_guide ?? undefined,
                blindGuidanceSystem: offering.accessibility_features.blind_guidance_system ?? undefined,
                otherOrientationAids: offering.accessibility_features.other_orientation_aids ?? undefined,
                interpreterAccess: CulturalInterpreterAccessMapper.toLabel(offering.accessibility_features.interpreter_access),
                informationMaterialSimpleLanguage: offering.accessibility_features.information_material_simple_language ?? undefined,
                websiteSimpleLanguage: offering.accessibility_features.website_simple_language ?? undefined,
                inclusionContact: {
                    id: offering.accessibility_features.inclusion_contact.id,
                    position: offering.accessibility_features.inclusion_contact.contact_function,
                    firstName: offering.accessibility_features.inclusion_contact.first_name,
                    lastName: offering.accessibility_features.inclusion_contact.last_name,
                    phone: offering.accessibility_features.inclusion_contact.phone,
                    email: offering.accessibility_features.inclusion_contact.email,
                },
            },
            openingHours: offering.opening_hours ?? undefined,
            offerPeriod: offering.offer_period ?? undefined,
            cultureCategories: offering.culture_categories.map(item => item.name),
        });
    }
}
